/**
 * ModelSelector - Dropdown selector for Gemini models
 * Converted from TSX to JSX for college_recruiter_v1
 *
 * Features:
 * - Dropdown selection for Gemini model variants (flash, pro, max)
 * - Model info display with capabilities and limitations
 * - Speed/quality badges
 * - Hover to expand details
 *
 * @module v2/analyzer/ModelSelector
 */

import React, { useState, useRef, useEffect } from 'react';
import { GEMINI_MODELS, getAvailableModels } from '../../../lib/geminiService';

// Icons

/**
 * ChevronDown icon - dropdown indicator
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const ChevronDownIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/**
 * Check icon - selection indicator
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const CheckIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Sparkles icon - header decoration
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const SparklesIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5L12 2z" />
  </svg>
);

/**
 * Zap icon - flash/speed model
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const ZapIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

/**
 * CPU icon - pro/balanced model
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const CpuIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <line x1="9" y1="1" x2="9" y2="4" />
    <line x1="15" y1="1" x2="15" y2="4" />
    <line x1="9" y1="20" x2="9" y2="23" />
    <line x1="15" y1="20" x2="15" y2="23" />
    <line x1="20" y1="9" x2="23" y2="9" />
    <line x1="20" y1="14" x2="23" y2="14" />
    <line x1="1" y1="9" x2="4" y2="9" />
    <line x1="1" y1="14" x2="4" y2="14" />
  </svg>
);

/**
 * Rocket icon - max/thorough model
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const RocketIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

/**
 * AlertCircle icon - limitations indicator
 * @param {Object} props
 * @param {string} [props.className] - CSS class name
 */
const AlertCircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/** Model icon mapping */
const MODEL_ICONS = {
  flash: ZapIcon,
  pro: CpuIcon,
  max: RocketIcon,
};

/** Speed color mapping */
const SPEED_COLORS = {
  fastest: 'text-green-400',
  fast: 'text-green-300',
  balanced: 'text-primary',
  thorough: 'text-yellow-400',
};

/** Quality badge styling */
const QUALITY_BADGES = {
  good: { bg: 'bg-green-500/20', text: 'text-green-400' },
  excellent: { bg: 'bg-primary/20', text: 'text-primary' },
  best: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
};

/**
 * ModelSelector Component
 * @param {Object} props
 * @param {string} props.selectedModel - Current model ID ('flash', 'pro', 'max')
 * @param {Function} props.onModelChange - Model change callback (model) => void
 * @param {boolean} [props.disabled=false] - Whether selector is disabled
 */
export const ModelSelector = ({
  selectedModel,
  onModelChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const dropdownRef = useRef(null);

  const selectedConfig = GEMINI_MODELS[selectedModel];
  const ModelIcon = MODEL_ICONS[selectedModel];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowDetails(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle model selection
   * @param {string} model - Model ID
   */
  const handleSelect = (model) => {
    onModelChange(model);
    setIsOpen(false);
    setShowDetails(null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium
          border transition-all duration-200
          ${disabled
            ? 'opacity-50 cursor-not-allowed bg-muted/50 border-border'
            : isOpen
              ? 'bg-primary/10 border-primary/50 text-primary shadow-[0_0_15px_rgba(255,215,0,0.15)]'
              : 'bg-muted/50 border-border hover:border-primary/30 hover:bg-muted text-foreground'
          }
        `}
      >
        <div className={`w-5 h-5 ${SPEED_COLORS[selectedConfig.speed]}`}>
          <ModelIcon className="w-full h-full" />
        </div>
        <span className="hidden sm:inline">{selectedConfig.name}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 z-[200] min-w-[320px] sm:min-w-[400px] max-h-[60vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Select Model</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Choose analysis quality: Quick (Flash) or Thorough (Pro)
            </p>
          </div>

          {/* Model Options */}
          <div className="p-2">
            {Object.keys(GEMINI_MODELS).map((model) => {
              const config = GEMINI_MODELS[model];
              const Icon = MODEL_ICONS[model];
              const isSelected = model === selectedModel;
              const isHovered = showDetails === model;

              return (
                <div key={model}>
                  <button
                    onClick={() => handleSelect(model)}
                    onMouseEnter={() => setShowDetails(model)}
                    onMouseLeave={() => setShowDetails(null)}
                    className={`
                      w-full flex items-start gap-3 p-3 rounded-xl transition-all duration-200
                      ${isSelected
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-muted/50 border border-transparent'
                      }
                    `}
                  >
                    {/* Icon */}
                    <div className={`
                      w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'bg-primary/20' : 'bg-muted'}
                    `}>
                      <Icon className={`w-5 h-5 ${SPEED_COLORS[config.speed]}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {config.name}
                        </span>
                        {config.recommended && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary/20 text-primary rounded-full uppercase">
                            Recommended
                          </span>
                        )}
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${QUALITY_BADGES[config.quality].bg} ${QUALITY_BADGES[config.quality].text}`}>
                          {config.quality}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{config.description}</p>
                    </div>

                    {/* Checkmark */}
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <CheckIcon className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </button>

                  {/* Expanded Details */}
                  {isHovered && config.videoCapabilities && (
                    <div className="mx-3 mb-2 mt-1 p-3 bg-muted/30 rounded-xl border border-border/50 animate-in fade-in duration-150">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Capabilities */}
                        <div>
                          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                            Capabilities
                          </h4>
                          <ul className="space-y-1">
                            {config.videoCapabilities.slice(0, 4).map((cap, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-xs text-foreground">
                                <CheckIcon className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                                <span>{cap}</span>
                              </li>
                            ))}
                            {config.videoCapabilities.length > 4 && (
                              <li className="text-xs text-muted-foreground">
                                +{config.videoCapabilities.length - 4} more
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Limitations */}
                        {config.limitations && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                              Limitations
                            </h4>
                            <ul className="space-y-1">
                              {config.limitations.map((lim, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                  <AlertCircleIcon className="w-3 h-3 text-yellow-400 flex-shrink-0 mt-0.5" />
                                  <span>{lim}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-border bg-muted/20">
            <p className="text-[10px] text-muted-foreground text-center">
              Higher quality models provide more accurate analysis but take longer to process
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
